import { STORAGE_VERIFY_CONNECTION } from '../../components/common/Storage';
import { customFetch } from '../common/customFetch';
import { getFromStorage, setInStorage } from '../common/StorageHelper';

/**
 * Checks if a script URL is reachable using fetch (no-cors mode for cross-origin)
 * @param {string} url - The script URL to check
 * @param {number} [timeout=5000] - Timeout in milliseconds
 * @returns {Promise<{ok: boolean, reason: string}>} Result object with ok status and reason
 *   - ok: true, reason: 'SCRIPT_REACHABLE' - Script is reachable
 *   - ok: false, reason: 'SCRIPT_ERROR' - Script failed to load
 *   - ok: false, reason: 'TIMEOUT' - Timeout reached
 */
const isScriptReachable = async (url, timeout = 5000) => {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({
        ok: false,
        reason: 'TIMEOUT',
      });
    }, timeout);

    // Use fetch with no-cors mode for cross-origin resources
    fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store'
    })
      .then(() => {
        finish({
          ok: true,
          reason: 'SCRIPT_REACHABLE',
        });
      })
      .catch((err) => {
        finish({
          ok: false,
          reason: 'SCRIPT_ERROR',
          error: err.message,
        });
      });
  });
};

/**
 * Checks WebSocket connectivity by establishing a connection and performing a handshake
 * @param {string} url - The WebSocket URL (ws:// or wss://)
 * @param {string} protocol - The WebSocket subprotocol
 * @param {number} [timeout=5000] - Timeout in milliseconds
 * @param {object} [heartbeatMsg={action: 'nmcHb', message: 'PING'}] - Heartbeat message to send on connection. Server should respond with 'ACK'
 * @returns {Promise<{ok: boolean, reason: string, protocol?: string, error?: string}>} Result object
 *   - ok: true, reason: 'HANDSHAKE_COMPLETE', protocol - Connection successful
 *   - ok: false, reason: 'TIMEOUT_WAITING_HANDSHAKE' - Timeout reached
 *   - ok: false, reason: 'websocket error' - Connection error
 *   - ok: false, reason: 'CLOSED_BEFORE_HANDSHAKE' - Connection closed before handshake
 *   - ok: false, reason: 'ERROR_TRYING_HANDSHAKE', error - Exception thrown
 */
const checkWebSocket = (
  url,
  protocol,
  timeout = 60000,
  heartbeatMsg = { action: 'nmcHb', message: 'PING' },
) => {
  return new Promise((resolve) => {
    let socket;
    let settled = false;

    const finish = (result) => {
      if (settled) return;

      settled = true;
      clearTimeout(timer);

      if (socket?.readyState === WebSocket.OPEN) {
        socket.close(1000, 'HANDSHAKE_COMPLETE');
      }

      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({
        ok: false,
        reason: 'TIMEOUT_WAITING_HANDSHAKE',
      });
    }, timeout);

    try {
      socket = new WebSocket(url, protocol);

      socket.onopen = () => {
        socket.send(JSON.stringify(heartbeatMsg));
      };

      socket.onmessage = (event) => {
        if (event.data === 'ACK') {
          finish({
            ok: true,
            reason: 'HANDSHAKE_COMPLETE'
          });
        } else {
          finish({
            ok: true,
            reason: 'HANDSHAKE_INCOMPLETE',
            info: {
              closeReason: `Expected "ACK" but got: ${event.data}`
            }
          });
        }
      };

      socket.onerror = (error) => {
        finish({
          ok: false,
          reason: 'WEBSOCKET_ERROR',
          error: error.message
        });
      };

      socket.onclose = (event) => {
        if (!settled) {
          finish({
            ok: false,
            reason: 'CLOSED_BEFORE_HANDSHAKE',
            info: {
              code: event.code,
              closeReason: event.reason || 'No reason provided',
              wasClean: event.wasClean,
            }
          });
        }
      };
    } catch (err) {
      console.log(err);
      finish({
        ok: false,
        reason: 'ERROR_TRYING_HANDSHAKE',
        error: err.message,
      });
    }
  });
};

/**
 * Checks if an image URL is accessible by attempting to load it
 * Note: This will fail for CORS-restricted images even if they are reachable
 * @param {string} url - The image URL to check
 * @param {number} [timeout=5000] - Timeout in milliseconds
 * @returns {Promise<{ok: boolean, reason: string}>} Result object with ok status and reason
 *   - ok: true, reason: 'IMAGE_LOADED' - Image loaded successfully
 *   - ok: false, reason: 'IMAGE_ERROR' - Image failed to load (could be network error or CORS)
 *   - ok: false, reason: 'TIMEOUT' - Timeout reached
 */
const canAccessImage = (url, timeout = 5000) => {
  return new Promise((resolve) => {
    const img = new Image();

    const timer = setTimeout(() => {
      cleanup();
      resolve({
        ok: false,
        reason: 'TIMEOUT',
      });
    }, timeout);

    const cleanup = () => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      cleanup();
      resolve({
        ok: true,
        reason: 'IMAGE_LOADED',
      });
    };

    img.onerror = () => {
      cleanup();
      resolve({
        ok: false,
        reason: 'IMAGE_ERROR',
      });
    };

    img.src = url;
  });
};

export const canCommunicate = async () => {
  // Step 1: Call the API
  const { data, errorObj } = await customFetch({
    url: `${window.env.REACT_APP_VERIFYCONNECT_URL}`,
    headers: {
      'X-Nmc-Pwa-Version': `UI build ${process.env.REACT_APP_BUILD_VERSION} (v${process.env.REACT_APP_VERSION})`,
    },
  });
  const apiResult = errorObj
    ? {
        ok: false,
        reason: 'API_ERROR',
        error: errorObj.message || 'Failed to reach delivery service API',
      }
    : {
        ok: true,
        reason: 'API_SUCCESS',
      };

  // Step 2: Call WebSocket (regardless of API result)
  let wsResult;
  if (errorObj || !data?.wssURL || !data?.authToken) {
    wsResult = {
      ok: false,
      reason: 'MISSING_WEBSOCKET_CREDENTIALS',
      error: 'Cannot test WebSocket without API credentials',
    };
  } else {
    wsResult = await checkWebSocket(data.wssURL, [data.authToken, 'validate']);
  }

  // Return both results
  return [
    {key: 'DELIVERY_SERVICE', result: apiResult, label: 'Waypoint testing services:'},
    {key: 'DELIVERY_MESSAGING_SERVICE', result: wsResult, label: 'Waypoint communication services:'},
  ];
};

export const verifyConnection = async (reTrigger = false) => {
  let connectionStatus = getFromStorage(STORAGE_VERIFY_CONNECTION);
  if (!connectionStatus || reTrigger) {
    connectionStatus = {};

    // Define test configurations with descriptive keys
    const tests = [
      { key: 'DELIVERY_COMMUNICATION', test: async () => await canCommunicate(), aggregatedResults: true },
      { key: 'EXTERNAL_TTS_SERVICE', test: async () => await isScriptReachable(window.env.REACT_APP_TEXTTOSPEECH_URL), label: 'Waypoint Text-to-Speech:' },
      { key: 'EXTERNAL_DELIVERY_SERVICE', test: async () => await isScriptReachable(`${window.env.REACT_APP_LEARNOSITY_ITEMS_API_PREFIX}?${window.env.REACT_APP_LTS_VERSION}`), label: 'Waypoint test engine:' },
      { key: 'DELIVER_ASSETS', test: async () => await canAccessImage(`${window.location.origin}/assets/student_app_logo.png`), label: 'Waypoint asset services:' },
      { key: 'CONTENT_ASSETS', test: async () => await canAccessImage(window.env.REACT_APP_AUTHOR_ASSET_URL), label: 'Waypoint content asset services:' },
    ];

    // Run all tests in parallel
    const results = await Promise.allSettled(await tests.map(t => t.test()));

    // Map results to key-value pairs
    results.forEach((result, index) => {
      const testKey = tests[index].key;

      if (result.status === 'fulfilled') {
        // Special handling for aggregatedResults which returns an array of results
        if (tests[index].aggregatedResults) {
          result.value.forEach((obj) => {
            connectionStatus[obj.key] = {
              ...obj.result,
              label: obj.label,
            };
          });
        } else {
          connectionStatus[testKey] = {
            ...result.value,
            label: tests[index].label,
          };
        }
      } else {
        connectionStatus[testKey] = {
          ok: false,
          reason: 'TEST_REJECTED',
          error: result.reason?.message || 'Unknown error',
          label: tests[index].label,
        };
      }
    });

    // Save to session storage
    setInStorage(STORAGE_VERIFY_CONNECTION, connectionStatus);
  }

  return connectionStatus;
};
